import NumberFlow from '@number-flow/react'
import { TICKER_NUMBER_FLOW_TIMING } from "@/app/playbook/indicators/TickerNumber"
import {
  useId,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import { CAPABILITY_STAGE_LADDERS, capabilityStageScaleForPlan, getCapabilityStageLabelForPlanValue } from "@/app/playbook/extrapolated-graph/capabilityThresholds"
import { currentLevelRiskAt } from '@/app/playbook/extrapolated-graph/takeoverRiskLikelihoodData'
import {
  CAPABILITIES_CURRENT_DOT_RADIUS,
  CAPABILITIES_FUTURE_DOT_RADIUS,
  CAPABILITIES_LINE_COLOR,
  CAPABILITIES_PAST_DOT_RADIUS,
  EPILOGUE_GRAPH_MOVE_DURATION_MS,
  EPILOGUE_MOVE_EASING,
  epilogueMoveEase,
} from './graphConstants'
import { devPerfMeasure, formatSpeedupHoverText } from './graphUtils'
import { getLabelStroke } from './renderUtils'
import { PointMarkerCircle } from './PointMarkerCircle'
import { RISK_FOOM_DESIGNS, RiskAmbientOverlay, RiskFoomPreviewOverlay, RiskSpaceExplorerOverlay, RiskTedGuideOverlay, RiskZoneContinuousGuideOverlay } from './RiskFoomPreviewOverlay'
import { AnnotationLabel } from './AnnotationLabel'
import { YearPointTooltip } from './YearPointTooltip'
import {
  computeDitherFieldGLLayer,
  drawRedAboveLossOfControlDitherCanvasLayer,
  type RedAboveLossOfControlCanvasLayer,
  getRiskLabelForComputeValue,
  getVisibleGlobeGeometryCanvases,
} from './RedAboveLossOfControlOverlay'
import {
  createDitherFieldGLRenderer,
  type DitherFieldGLLayer,
  type DitherFieldGLRenderer,
} from './ditherFieldGLRenderer'
import {
  useBandHudOverlayPublisher,
  type PointHoverTooltipPayload,
} from './BandHudOverlayChannel'
import { GraphPointHoverTooltip } from './GraphPointHoverTooltip'
import type { ExtrapolatedGraphHoverPoint, ExtrapolatedGraphRenderProps } from './types'

type GraphCanvasLayerProps = {
  width: number
  height: number
  lineSegments: ExtrapolatedGraphRenderProps['lineSegments']
  pixelRatio: number
  redAboveLossOfControlCanvasLayer?: ExtrapolatedGraphRenderProps[
    'redAboveLossOfControlCanvasLayer'
  ]
  blackBelowTooSlowRiskCanvasLayer?: ExtrapolatedGraphRenderProps[
    'blackBelowTooSlowRiskCanvasLayer'
  ]
  // Extra stroke width added to every line segment — the point-hover line
  // highlight (see highlightLineWhilePointHovered in types.ts).
  lineExtraStrokePx?: number
  // While set, DASHED segments (the future capability line) only paint left
  // of this x — the foom preview's morph designs clip the line at the
  // takeoff point and continue it themselves (see riskFoomDesign in
  // types.ts / RISK_FOOM_DESIGNS).
  dashedLineClipMaxX?: number
}

// How much the capability line thickens while the point hover is engaged and
// highlightLineWhilePointHovered is set — enough to read as "lit up" without
// changing the graph's proportions.
const LINE_HOVER_HIGHLIGHT_EXTRA_STROKE_PX = 2.5

// ─── Takeover-risk area hover (see takeoverRiskAreaHover in types.ts) ───────
// While the pointer is in the red risk field above the capability line, the
// whole field lightens to this fraction of its normal strength.
export const TAKEOVER_RISK_AREA_HOVER_OPACITY = 0.6
// The pointer must be this many px ABOVE the line before the area hover
// engages — a landing strip so aiming at the line itself still reads as the
// point-on-line hover.
export const TAKEOVER_RISK_AREA_LINE_CLEARANCE_PX = 12
// The zone-clarity round (riskZoneDimTween in types.ts, tabs 66–70): how long
// the field lighten — and the line-hover stroke highlight with it — takes to
// ease between its two strengths instead of snapping at the zone boundary.
export const RISK_ZONE_DIM_TWEEN_MS = 160
// The dot-shrink dim (riskZoneDimDotShrink, tab 2 of the picks branch —
// Joanna 2026-07-15): while the field is hovered its dither DOTS shrink to
// this fraction of their radius instead of the whole layer losing opacity —
// the field visibly thins, full ink kept.
export const RISK_ZONE_DOT_SHRINK_RADIUS_SCALE = 0.6

// rAF tween toward `target` while `enabled` (an ease-out cubic, so a value
// leaves fast and lands gently). Disabled, it passes the target through — the
// pre-tween snap behavior. Drives the canvas dither's dim factor and the line
// highlight's extra stroke, which live in canvas redraws where CSS
// transitions can't reach.
const useTweenedNumber = (target: number, ms: number, enabled: boolean): number => {
  const [value, setValue] = useState(target)
  const valueRef = useRef(target)
  useEffect(() => {
    if (!enabled) {
      valueRef.current = target
      setValue(target)
      return
    }
    const from = valueRef.current
    if (from === target) return
    let raf = 0
    const startedAt = performance.now()
    const step = (now: number) => {
      const linear = Math.min(1, (now - startedAt) / ms)
      const eased = 1 - (1 - linear) ** 3
      const next = from + (target - from) * eased
      valueRef.current = next
      setValue(next)
      if (linear < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, ms, enabled])
  return enabled ? value : target
}

const HOVER_RISK_BUCKET_LABELS = [
  'Low Risk',
  'Moderate Risk',
  'High Risk',
  'High Risk',
  'Very High Risk',
]
const EXTREME_RISK_BUCKET_INDEX = HOVER_RISK_BUCKET_LABELS.length - 1
const MODERATE_RISK_COLOR = '#ff8a8a'
const ELEVATED_OR_HIGH_RISK_COLOR = '#ff2a2a'
const EXTREME_RISK_TOOLTIP_COLOR = 'var(--race-background)'
const GRAPH_LABEL_LAYER_Z_INDEX = 90

// Keeps the current-point dot painted ABOVE the capability line (the line
// canvas is z-index 1, the axis svg 2).
const CURRENT_POINT_MARKER_Z_INDEX = 3

// Capability-threshold label beside the current-point dot
// (showCurrentPointCapabilityLabel mode) — e.g. "Reliable Agent" /
// "Automated coder"; the text comes from capabilityThresholds.tsx. It
// prefers sitting ABOVE-LEFT of the dot, falling back to CENTERED-ABOVE,
// ABOVE-RIGHT, and then the mirrored BELOW slots; a placement is skipped
// when it would leave the graph's bounding box or overlap the globe (which
// paints above the graph and would swallow the label — e.g. Plan A around
// 2035, where the rising line walks the label into it).
const CURRENT_POINT_CAPABILITY_LABEL_GAP_X_PX = 6 // dot center → label edge
const CURRENT_POINT_CAPABILITY_LABEL_GAP_Y_PX = 5 // dot center → label bottom
const CURRENT_POINT_CAPABILITY_LABEL_PADDING = '1px 5px'
const CURRENT_POINT_CAPABILITY_LABEL_H_PADDING_TOTAL_PX = 10
const CURRENT_POINT_CAPABILITY_LABEL_BORDER_RADIUS_PX = 4
const CURRENT_POINT_CAPABILITY_LABEL_BACKGROUND = 'var(--playbook-epilogue-bg, #fffff8)'
const CURRENT_POINT_CAPABILITY_LABEL_TEXT_COLOR = '#000'
const CURRENT_POINT_CAPABILITY_LABEL_FONT_SIZE_PX = 8.5
// Estimated rendered width of one character, the SSR fallback for the
// measureText path below.
const CURRENT_POINT_CAPABILITY_LABEL_CHAR_WIDTH_PX = 5.1
// Estimated rendered height (one 8.5px mono line plus the vertical padding;
// measures 14.75px), used for the bounds and globe-overlap checks.
const CURRENT_POINT_CAPABILITY_LABEL_HEIGHT_PX = 15
// Breathing room around the label for the globe-overlap test (the bounds
// test uses the un-inflated box), so a placement that would touch the
// globe's edge is skipped, not just one that covers it.
const CURRENT_POINT_CAPABILITY_LABEL_GLOBE_CLEARANCE_PX = 4
// The label's slot is re-evaluated only once the dot has been still this
// long — the scroll animation's intermediate frames never move the label
// between slots (it rides the dot in its current slot), so a transition
// causes at most one slot change, at the stable position.
const CURRENT_POINT_CAPABILITY_LABEL_SETTLE_MS = 250
// The label's font for canvas measureText — bold 8.5px in Tailwind's
// `font-mono` stack (this app doesn't override it), matching the label div's
// classes. Placement decisions treat the graph's bounding box as a hard
// constraint, so the width must be the real rendered width, not a
// per-character guess: an overestimate rules out placements that actually
// fit, an underestimate lets the label spill past the edge.
const CURRENT_POINT_CAPABILITY_LABEL_MEASURE_FONT =
  `bold ${CURRENT_POINT_CAPABILITY_LABEL_FONT_SIZE_PX}px ui-monospace, `
  + 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", '
  + '"Courier New", monospace'
const currentPointCapabilityLabelWidthCache = new Map<string, number>()
let labelMeasureContext: CanvasRenderingContext2D | null = null

// Full label box width (text + horizontal padding) for a given label text.
const getCurrentPointCapabilityLabelWidth = (text: string): number => {
  const estimated =
    text.length * CURRENT_POINT_CAPABILITY_LABEL_CHAR_WIDTH_PX
    + CURRENT_POINT_CAPABILITY_LABEL_H_PADDING_TOTAL_PX
  if (typeof document === 'undefined') return estimated
  const cached = currentPointCapabilityLabelWidthCache.get(text)
  if (cached !== undefined) return cached
  if (!labelMeasureContext) {
    labelMeasureContext = document.createElement('canvas').getContext('2d')
  }
  if (!labelMeasureContext) return estimated
  labelMeasureContext.font = CURRENT_POINT_CAPABILITY_LABEL_MEASURE_FONT
  const width =
    labelMeasureContext.measureText(text).width
    + CURRENT_POINT_CAPABILITY_LABEL_H_PADDING_TOTAL_PX
  currentPointCapabilityLabelWidthCache.set(text, width)
  return width
}

// Current-year pill pinned to the graph's top-left (currentYearPill mode).
// The 2026-07-09 cohesion restyle (title-tier, picked from the tab 1–5
// iteration): 14px/700 with a tighter 2.5×9 pad and 6px radius, sized to sit
// with the HUD's header tier instead of out-inking everything on the canvas.
// Size + weight live in the className on the pill button/div below; the
// paddings here also set the epilogue flush-left offset.
// In epilogue mode (currentYearPillFlushLeft) the background drops and the
// pill slides left so the year TEXT's left edge sits flush with the graph's
// left edge (the negative left cancels the pill's own horizontal padding).
const CURRENT_YEAR_PILL_LEFT_PX = 14
const CURRENT_YEAR_PILL_V_PADDING_PX = 2.5
const CURRENT_YEAR_PILL_H_PADDING_PX = 9
const CURRENT_YEAR_PILL_BACKGROUND = 'var(--playbook-epilogue-bg, #fffff8)'
// Vertical placement of the year pill. Normally pinned to the graph's top.
// In epilogue mode it drops down to sit just above the capabilities line at
// its LEFT end (currentYearPillAnchorY is the line's y there — the pill is
// flush-left, so it anchors to the left end, not the rightmost current point
// which rides up to the graph top); this gap lifts the pill's bottom clear
// of the line.
// The drop is a rAF tween (usePillDropProgress), NOT a CSS transition: the
// anchor moves every frame while the graph shrinks and the date animates,
// and retargeting a CSS transition per frame restarts its easing — the pill
// crawled and could be caught hanging mid-graph. With the tween, the fully
// dropped pill tracks the moving anchor exactly, and enter/exit blend from
// wherever the pill currently is on the epilogue move clock.
const CURRENT_YEAR_PILL_TOP_PX = 12
const CURRENT_YEAR_PILL_EPILOGUE_GAP_ABOVE_LINE_PX = 10
// Month prefix for the branch plans' sub-year readout ("Nov 2030") — see
// currentYearPillMonthIndex.
const CURRENT_YEAR_PILL_MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

// 0 → pinned to the graph's top-left, 1 → dropped to the epilogue anchor.
// Tweened toward the mode's target on the epilogue move clock, retargeting
// from the current value on reversal (same pattern as the epilogue graph
// height tween in playbookHUDInfra).
const usePillDropProgress = (isDropped: boolean) => {
  const [progress, setProgress] = useState(() => (isDropped ? 1 : 0))
  const progressRef = useRef(progress)
  useEffect(() => {
    const target = isDropped ? 1 : 0
    const from = progressRef.current
    if (from === target) return
    const startTime = performance.now()
    let frameId: number
    const tick = (now: number) => {
      const tweenProgress = Math.min(
        1,
        (now - startTime) / EPILOGUE_GRAPH_MOVE_DURATION_MS
      )
      const value = from + (target - from) * epilogueMoveEase(tweenProgress)
      progressRef.current = value
      setProgress(value)
      if (tweenProgress < 1) frameId = requestAnimationFrame(tick)
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [isDropped])
  return progress
}

const getRiskBucketIndex = (riskFraction: number) => {
  if (riskFraction <= 0) return -1
  return Math.max(0, Math.min(
    HOVER_RISK_BUCKET_LABELS.length - 1,
    Math.floor(riskFraction * 10)
  ))
}

const getRiskBucketLabel = (riskFraction: number) => {
  const bucketIndex = getRiskBucketIndex(riskFraction)
  if (bucketIndex < 0) return 'Negligible Risk'
  return HOVER_RISK_BUCKET_LABELS[bucketIndex]
}

const getIsModerateOrHigherRisk = (riskFraction: number) =>
  riskFraction >= 0.1
const getRiskLabelColor = (riskBucketIndex: number) => {
  if (riskBucketIndex === 1) return MODERATE_RISK_COLOR
  if (riskBucketIndex > 1 && riskBucketIndex < EXTREME_RISK_BUCKET_INDEX) {
    return ELEVATED_OR_HIGH_RISK_COLOR
  }
  return undefined
}

const parseSvgStrokeDasharray = (strokeDasharray: string | undefined) => {
  if (!strokeDasharray) return []
  return strokeDasharray
    .split(/[\s,]+/)
    .map(value => Number.parseFloat(value))
    .filter(value => Number.isFinite(value) && value > 0)
}

const SVG_PATH_TOKEN_PATTERN = /[MLC]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi

const drawGeneratedGraphPath = (
  context: CanvasRenderingContext2D,
  pathData: string
) => {
  const tokens = pathData.match(SVG_PATH_TOKEN_PATTERN)
  if (!tokens) return
  let tokenIndex = 0
  context.beginPath()
  while (tokenIndex < tokens.length) {
    const command = tokens[tokenIndex]
    tokenIndex += 1
    if (command === 'M') {
      const x = Number.parseFloat(tokens[tokenIndex])
      const y = Number.parseFloat(tokens[tokenIndex + 1])
      tokenIndex += 2
      if (Number.isFinite(x) && Number.isFinite(y)) {
        context.moveTo(x, y)
      }
      continue
    }
    if (command === 'L') {
      const x = Number.parseFloat(tokens[tokenIndex])
      const y = Number.parseFloat(tokens[tokenIndex + 1])
      tokenIndex += 2
      if (Number.isFinite(x) && Number.isFinite(y)) {
        context.lineTo(x, y)
      }
      continue
    }
    if (command === 'C') {
      const cp1X = Number.parseFloat(tokens[tokenIndex])
      const cp1Y = Number.parseFloat(tokens[tokenIndex + 1])
      const cp2X = Number.parseFloat(tokens[tokenIndex + 2])
      const cp2Y = Number.parseFloat(tokens[tokenIndex + 3])
      const endX = Number.parseFloat(tokens[tokenIndex + 4])
      const endY = Number.parseFloat(tokens[tokenIndex + 5])
      tokenIndex += 6
      if (
        Number.isFinite(cp1X)
        && Number.isFinite(cp1Y)
        && Number.isFinite(cp2X)
        && Number.isFinite(cp2Y)
        && Number.isFinite(endX)
        && Number.isFinite(endY)
      ) {
        context.bezierCurveTo(cp1X, cp1Y, cp2X, cp2Y, endX, endY)
      }
      continue
    }
  }
  context.stroke()
}

const GraphCanvasLayer = ({
  width,
  height,
  lineSegments,
  pixelRatio,
  redAboveLossOfControlCanvasLayer,
  blackBelowTooSlowRiskCanvasLayer,
  lineExtraStrokePx = 0,
  dashedLineClipMaxX,
}: GraphCanvasLayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const glCanvasRef = useRef<HTMLCanvasElement | null>(null)
  // null = not tried yet; 'unavailable' = WebGL2 init failed, use canvas 2D.
  const glRendererRef = useRef<DitherFieldGLRenderer | 'unavailable' | null>(null)
  // Bumped when the browser force-loses our WebGL context (pages over the
  // per-page context limit lose their OLDEST context — globes + graphs get
  // there). The frame drawn around the loss is blank (a dead context renders
  // nothing and the blit paints transparent black), and if the story is
  // standing still nothing else re-runs the redraw effect — so the blank
  // frame would just sit there. Bumping this re-runs the redraw, which then
  // takes the canvas-2D path.
  const [glContextGeneration, setGlContextGeneration] = useState(0)
  const canvasWidth = Math.max(1, Math.ceil(width * pixelRatio))
  const canvasHeight = Math.max(1, Math.ceil(height * pixelRatio))

  useEffect(() => {
    const glCanvas = glCanvasRef.current
    const handleContextLost = (event: Event) => {
      // preventDefault signals we handle restoration, so the browser fires
      // webglcontextrestored once it revives the evicted context (pages over
      // the per-page WebGL limit lose their oldest context) — and the redraw
      // below bridges the gap on the canvas-2D path.
      event.preventDefault()
      setGlContextGeneration(g => g + 1)
    }
    const handleContextRestored = () => {
      // Back to the GPU path: drop 'unavailable' so the next redraw
      // recreates the renderer on the restored context.
      if (glRendererRef.current === 'unavailable') glRendererRef.current = null
      setGlContextGeneration(g => g + 1)
    }
    glCanvas?.addEventListener('webglcontextlost', handleContextLost)
    glCanvas?.addEventListener('webglcontextrestored', handleContextRestored)
    return () => {
      glCanvas?.removeEventListener('webglcontextlost', handleContextLost)
      glCanvas?.removeEventListener('webglcontextrestored', handleContextRestored)
      if (glRendererRef.current && glRendererRef.current !== 'unavailable') {
        glRendererRef.current.dispose()
        glRendererRef.current = null
      }
    }
  }, [])

  // Display scale (CSS transforms between the canvas and the viewport),
  // measured only when the layout inputs change: this effect used to call
  // getBoundingClientRect on EVERY redraw — a forced layout per animation
  // frame in performance traces.
  const displayScaleRef = useRef<{
    key: string
    scaleX: number
    scaleY: number
  } | null>(null)

  useEffect(() => {
    const redrawStart = performance.now()
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    const displayScaleKey = `${width}x${height}@${pixelRatio}`
    if (displayScaleRef.current?.key !== displayScaleKey) {
      const rect = canvas.getBoundingClientRect()
      displayScaleRef.current = {
        key: displayScaleKey,
        scaleX: width > 0 && rect.width > 0 ? Math.max(1, rect.width / width) : 1,
        scaleY: height > 0 && rect.height > 0 ? Math.max(1, rect.height / height) : 1,
      }
    }
    const backingScaleX = pixelRatio * displayScaleRef.current.scaleX
    const backingScaleY = pixelRatio * displayScaleRef.current.scaleY
    // Only reallocate the backing store when its size actually changes;
    // assigning canvas.width is not a no-op even for equal values.
    const backingWidth = Math.max(1, Math.ceil(width * backingScaleX))
    const backingHeight = Math.max(1, Math.ceil(height * backingScaleY))
    if (canvas.width !== backingWidth) canvas.width = backingWidth
    if (canvas.height !== backingHeight) canvas.height = backingHeight
    const context = canvas.getContext('2d')
    if (!context) return
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.setTransform(backingScaleX, 0, 0, backingScaleY, 0, 0)
    context.lineCap = 'round'
    context.lineJoin = 'round'

    // Dither fields: GPU path first (see ditherFieldGLRenderer.ts — the CPU
    // only bakes per-tile radius grids; the fragment shader draws the dots).
    // Black is rendered before red so red composites on top where they
    // overlap, same as the canvas-2D order below.
    let glHandledDither = false
    const ditherLayers = [
      blackBelowTooSlowRiskCanvasLayer,
      redAboveLossOfControlCanvasLayer,
    ].filter(
      (layer): layer is RedAboveLossOfControlCanvasLayer => Boolean(layer)
    )
    // ?dither2d forces the canvas-2D dither path (perf A/B + escape hatch).
    const forceCanvas2dDither =
      typeof window !== 'undefined'
      && window.location.search.includes('dither2d')
    const glCanvas = forceCanvas2dDither ? null : glCanvasRef.current
    if (glCanvas) {
      if (glRendererRef.current === null) {
        glRendererRef.current =
          createDitherFieldGLRenderer(glCanvas) ?? 'unavailable'
      }
      // Pages over the per-page WebGL limit (globes + graphs + galaxies)
      // force-lose their OLDEST context. Rendering with a lost context is a
      // silent no-op, so blitting the dead GL canvas after the clearRect
      // above would leave the ENTIRE graph blank — line included. Take the
      // canvas-2D path until webglcontextrestored fires (see the listener
      // above). Deliberately NOT dispose()d: its explicit loseContext()
      // would block the browser from auto-restoring the context.
      if (
        glRendererRef.current !== null
        && glRendererRef.current !== 'unavailable'
        && glRendererRef.current.isContextLost()
      ) {
        glRendererRef.current = 'unavailable'
      }
      const glRenderer = glRendererRef.current
      if (glRenderer !== 'unavailable') {
        const bakeStart = performance.now()
        const glLayers = ditherLayers.map((ditherLayer) =>
          computeDitherFieldGLLayer(ditherLayer, { width, height })
        )
        devPerfMeasure('dither:gl-bake', { start: bakeStart })
        if (glLayers.every((glLayer) => glLayer !== null)) {
          const glSubmitStart = performance.now()
          try {
            glRenderer.render(
              glLayers as DitherFieldGLLayer[],
              width,
              height,
              backingScaleX,
              backingScaleY
            )
            // Composite the GL frame into the visible 2D canvas (a GPU-side
            // blit) instead of displaying the GL canvas itself — Safari
            // composites WebGL canvases out of z-order with overlapping
            // positioned siblings (e.g. the current-year pill), and this also
            // keeps the dither correctly under the line strokes drawn below.
            context.drawImage(glCanvas, 0, 0, width, height)
            // Eviction is async and can land mid-frame: a just-lost context
            // renders nothing and the blit silently paints transparent
            // black. Only trust the GL frame if the context is still alive
            // AFTER the blit; otherwise the canvas-2D path below repaints
            // this same frame.
            if (glRenderer.isContextLost()) {
              glRendererRef.current = 'unavailable'
            } else {
              glHandledDither = true
            }
          } catch {
            // drawImage from a dead canvas can also throw — same fallback.
            glRendererRef.current = 'unavailable'
          }
          devPerfMeasure('dither:gl-submit', { start: glSubmitStart })
        }
      }
    }

    if (!glHandledDither) {
      // Draw the black "too slow" field FIRST so the red "too fast" field
      // composites on top of it (black sits behind red where they overlap).
      for (const ditherLayer of ditherLayers) {
        drawRedAboveLossOfControlDitherCanvasLayer(context, ditherLayer)
      }
    }

    // No extra clip for clipLinesToDitherTop here: the red dither field is
    // 'top'-anchored, so its visible top edge IS the canvas top — and the
    // canvas raster already crops the strokes there. An earlier take clipped
    // beneath the field's loss-of-control curves instead; those run far
    // off-plot above the canvas mid-story (a no-op), and where they DO come
    // on-plot they are the field's lower density fade — the polyline clip
    // cut the line's steep late-story run mid-field (white channel through
    // the dither on Plan D at Jan 2032).
    for (const lineSegment of lineSegments) {
      try {
        context.save()
        // Morph foom designs clip the DASHED (future) line at the takeoff
        // x — see dashedLineClipMaxX in GraphCanvasLayerProps.
        if (dashedLineClipMaxX !== undefined && lineSegment.strokeDasharray) {
          context.beginPath()
          context.rect(0, 0, dashedLineClipMaxX, height)
          context.clip()
        }
        context.strokeStyle = lineSegment.stroke
        context.lineWidth = lineSegment.strokeWidth + lineExtraStrokePx
        context.globalAlpha = lineSegment.strokeOpacity ?? 1
        const dashPattern = parseSvgStrokeDasharray(lineSegment.strokeDasharray)
        context.setLineDash(dashPattern)
        // Dashed segments use SVG's default butt caps: round caps extend each
        // dash by a full stroke width (eating the gap), smearing the dashes
        // into soft pills instead of crisp rectangles.
        context.lineCap = dashPattern.length > 0 ? 'butt' : 'round'
        drawGeneratedGraphPath(context, lineSegment.d)
        context.restore()
      } catch {
        // Keep the graph usable if an editor-generated path is malformed.
      }
    }
    if (process.env.NODE_ENV === 'development') {
      // Debug tap for line-animation diagnosis (see scripts/ hunt tooling):
      // the most recent draw's segments, sampleable from the console/driver.
      ;(window as unknown as Record<string, unknown>).__graphLineSegments =
        lineSegments.map((lineSegment) => ({
          key: lineSegment.key,
          stroke: lineSegment.stroke,
          strokeDasharray: lineSegment.strokeDasharray,
          d: lineSegment.d,
        }))
    }
    devPerfMeasure(
      glHandledDither ? 'dither:canvas-redraw-gl' : 'dither:canvas-redraw',
      { start: redrawStart }
    )
  }, [
    height,
    lineSegments,
    pixelRatio,
    redAboveLossOfControlCanvasLayer,
    blackBelowTooSlowRiskCanvasLayer,
    width,
    lineExtraStrokePx,
    dashedLineClipMaxX,
    glContextGeneration,
  ])

  return (
    <>
      {/* Offscreen scratch surface for the GPU dither renderer; its frame is
          blitted into the visible 2D canvas below (see the redraw effect). */}
      <canvas ref={glCanvasRef} className="hidden" />
      <canvas
        ref={canvasRef}
        className="absolute left-0 top-0 pointer-events-none"
        width={canvasWidth}
        height={canvasHeight}
        style={{ zIndex: 1 }}
      />
    </>
  )
}

// Floor for the canvas backing resolution, on every surface: at 1x, the
// antialiasing on the capability line's strokes reads as blur next to the
// vector-crisp SVG chrome (worst on the choose-your-path emphasized line),
// on non-retina displays and under browser/pinch zoom alike. At 2x the
// browser's downscale acts as supersampling on 1x displays.
const GRAPH_CANVAS_MIN_PIXEL_RATIO = 2

const getGraphCanvasPixelRatio = () => {
  if (typeof window === 'undefined') return 1
  return Math.max(GRAPH_CANVAS_MIN_PIXEL_RATIO, window.devicePixelRatio || 1)
}

const useGraphCanvasPixelRatio = () => {
  const [pixelRatio, setPixelRatio] = useState(1)

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Re-check on resize: browser zoom changes devicePixelRatio and fires a
    // window resize.
    const updatePixelRatio = () => setPixelRatio(getGraphCanvasPixelRatio())

    updatePixelRatio()
    window.addEventListener('resize', updatePixelRatio)

    return () => {
      window.removeEventListener('resize', updatePixelRatio)
    }
  }, [])

  return pixelRatio
}

// Downscaled alpha snapshot of a globe canvas, as a grid of has-visible-
// pixels flags over the canvas box. The sphere covers only part of the
// square canvas (how much varies with the camera's keyframed zoom) and the
// satellites poke out past it, so a label-overlap test needs the actual
// alpha, not a fitted circle: the WebGL canvas (preserveDrawingBuffer, so it
// reads back) is downscaled into a small scratch raster. Cached briefly per
// canvas — the overlap test runs per animated frame while the globe's look
// changes on the scroll timescale. null = nothing visible on the canvas.
type GlobeAlphaGrid = {
  size: number
  // size×size row-major flags: 1 = this cell contains visible globe pixels.
  cells: Uint8Array
}
const GLOBE_ALPHA_GRID_TTL_MS = 300
const GLOBE_ALPHA_GRID_SIZE = 48
const globeAlphaGridCache = new WeakMap<
  HTMLCanvasElement,
  { measuredAt: number; grid: GlobeAlphaGrid | null }
>()
let globeAlphaScratchContext: CanvasRenderingContext2D | null = null

const getGlobeAlphaGrid = (
  globeCanvas: HTMLCanvasElement
): GlobeAlphaGrid | null => {
  const now = performance.now()
  const cached = globeAlphaGridCache.get(globeCanvas)
  if (cached && now - cached.measuredAt < GLOBE_ALPHA_GRID_TTL_MS) {
    return cached.grid
  }
  const size = GLOBE_ALPHA_GRID_SIZE
  const cells = new Uint8Array(size * size)
  let grid: GlobeAlphaGrid | null = null
  try {
    if (!globeAlphaScratchContext) {
      const scratchCanvas = document.createElement('canvas')
      scratchCanvas.width = size
      scratchCanvas.height = size
      globeAlphaScratchContext = scratchCanvas.getContext('2d', {
        willReadFrequently: true,
      })
    }
    const context = globeAlphaScratchContext
    if (context) {
      context.clearRect(0, 0, size, size)
      context.drawImage(globeCanvas, 0, 0, size, size)
      const pixels = context.getImageData(0, 0, size, size).data
      let anyVisible = false
      for (let cellIndex = 0; cellIndex < cells.length; cellIndex += 1) {
        if (pixels[cellIndex * 4 + 3] > 16) {
          cells[cellIndex] = 1
          anyVisible = true
        }
      }
      if (anyVisible) grid = { size, cells }
    }
  } catch {
    // Unreadable canvas: treat the whole box as occupied (conservative).
    cells.fill(1)
    grid = { size, cells }
  }
  globeAlphaGridCache.set(globeCanvas, { measuredAt: now, grid })
  return grid
}

// How many alpha-grid cells of visible globe geometry a graph-local rect
// covers; 0 = clear. Client rects are converted through the graph
// container's own client rect, which keeps ancestor transforms (the epilogue
// move/scale) out of the math; globes elsewhere on the page resolve to boxes
// far outside the graph and never intersect.
const getGraphLocalRectGlobeOverlapCellCount = (
  rect: { x: number; y: number; width: number; height: number },
  graphContainer: HTMLDivElement | null,
  graphWidth: number
): number => {
  if (!graphContainer || typeof document === 'undefined') return 0
  const containerRect = graphContainer.getBoundingClientRect()
  if (containerRect.width <= 0 || graphWidth <= 0) return 0
  const scale = containerRect.width / graphWidth
  const rectClientLeft = containerRect.left + rect.x * scale
  const rectClientTop = containerRect.top + rect.y * scale
  const rectClientRight = rectClientLeft + rect.width * scale
  const rectClientBottom = rectClientTop + rect.height * scale
  let overlapCellCount = 0
  for (const globeCanvas of getVisibleGlobeGeometryCanvases()) {
    const globeRect = globeCanvas.getBoundingClientRect()
    if (globeRect.width <= 0 || globeRect.height <= 0) continue
    if (
      rectClientRight <= globeRect.left
      || rectClientLeft >= globeRect.right
      || rectClientBottom <= globeRect.top
      || rectClientTop >= globeRect.bottom
    ) {
      continue
    }
    const grid = getGlobeAlphaGrid(globeCanvas)
    if (!grid) continue
    // Count the visible-globe flags among the grid cells the rect spans.
    const firstCol = Math.max(0, Math.floor(
      ((rectClientLeft - globeRect.left) / globeRect.width) * grid.size
    ))
    const lastCol = Math.min(grid.size - 1, Math.ceil(
      ((rectClientRight - globeRect.left) / globeRect.width) * grid.size
    ) - 1)
    const firstRow = Math.max(0, Math.floor(
      ((rectClientTop - globeRect.top) / globeRect.height) * grid.size
    ))
    const lastRow = Math.min(grid.size - 1, Math.ceil(
      ((rectClientBottom - globeRect.top) / globeRect.height) * grid.size
    ) - 1)
    for (let row = firstRow; row <= lastRow; row += 1) {
      for (let col = firstCol; col <= lastCol; col += 1) {
        if (grid.cells[row * grid.size + col]) overlapCellCount += 1
      }
    }
  }
  return overlapCellCount
}

type CurrentPointCapabilityLabelPlacement = {
  horizontal: 'left' | 'center' | 'right'
  vertical: 'above' | 'below'
}

// Candidate placements around the dot, in preference order: the three
// above-the-dot slots first, then the below-the-dot slots (the escape hatch
// for narrow layouts where the globe covers the whole strip above the line).
const CURRENT_POINT_CAPABILITY_LABEL_PLACEMENTS: readonly CurrentPointCapabilityLabelPlacement[] = [
  { horizontal: 'left', vertical: 'above' },
  { horizontal: 'center', vertical: 'above' },
  { horizontal: 'right', vertical: 'above' },
  { horizontal: 'left', vertical: 'below' },
  { horizontal: 'center', vertical: 'below' },
  { horizontal: 'right', vertical: 'below' },
]

// Slot for the label at a (settled) dot position. The label must stay
// inside the graph's bounding box (hard constraint); among the placements
// that do, take the first that fully clears the globe, else the one
// covering the least globe.
const chooseCurrentPointCapabilityLabelPlacement = ({
  cx,
  cy,
  labelWidth,
  graphWidth,
  graphHeight,
  graphContainer,
}: {
  cx: number
  cy: number
  labelWidth: number
  graphWidth: number
  graphHeight: number
  graphContainer: HTMLDivElement | null
}): CurrentPointCapabilityLabelPlacement => {
  const labelRectFor = (candidate: CurrentPointCapabilityLabelPlacement) => ({
    x: candidate.horizontal === 'left'
      ? cx - CURRENT_POINT_CAPABILITY_LABEL_GAP_X_PX - labelWidth
      : candidate.horizontal === 'center'
        ? cx - labelWidth / 2
        : cx + CURRENT_POINT_CAPABILITY_LABEL_GAP_X_PX,
    y: candidate.vertical === 'above'
      ? cy
        - CURRENT_POINT_CAPABILITY_LABEL_GAP_Y_PX
        - CURRENT_POINT_CAPABILITY_LABEL_HEIGHT_PX
      : cy + CURRENT_POINT_CAPABILITY_LABEL_GAP_Y_PX,
    width: labelWidth,
    height: CURRENT_POINT_CAPABILITY_LABEL_HEIGHT_PX,
  })
  const inBoundsPlacements = CURRENT_POINT_CAPABILITY_LABEL_PLACEMENTS.filter(
    candidate => {
      const rect = labelRectFor(candidate)
      return rect.x >= 0
        && rect.x + rect.width <= graphWidth
        && rect.y >= 0
        && rect.y + rect.height <= graphHeight
    }
  )
  let placement = CURRENT_POINT_CAPABILITY_LABEL_PLACEMENTS[0]
  let placementOverlapCells = Infinity
  for (const candidate of inBoundsPlacements) {
    const rect = labelRectFor(candidate)
    const clearance = CURRENT_POINT_CAPABILITY_LABEL_GLOBE_CLEARANCE_PX
    const overlapCells = getGraphLocalRectGlobeOverlapCellCount(
      {
        x: rect.x - clearance,
        y: rect.y - clearance,
        width: rect.width + clearance * 2,
        height: rect.height + clearance * 2,
      },
      graphContainer,
      graphWidth,
    )
    if (overlapCells < placementOverlapCells) {
      placement = candidate
      placementOverlapCells = overlapCells
    }
    if (overlapCells === 0) break
  }
  return placement
}

// The current-point capability label. Its slot around the dot is COMMITTED
// state, not a per-frame derivation: during the scroll-driven date
// animation the label just rides the dot in its current slot, and the slot
// is re-evaluated once, when the dot settles (see
// CURRENT_POINT_CAPABILITY_LABEL_SETTLE_MS) — evaluating per frame made the
// label hop between slots mid-transition. A slot change animates via the
// transform transition on the inner element; the outer anchor tracks the
// dot untransitioned so the label never lags the dot itself.
const CurrentPointCapabilityLabel = ({
  cx,
  cy,
  labelText,
  graphWidth,
  graphHeight,
  graphContainerRef,
}: {
  cx: number
  cy: number
  labelText: string
  graphWidth: number
  graphHeight: number
  graphContainerRef: MutableRefObject<HTMLDivElement | null>
}) => {
  const [placement, setPlacement] = useState<CurrentPointCapabilityLabelPlacement>(
    () => chooseCurrentPointCapabilityLabelPlacement({
      cx,
      cy,
      labelWidth: getCurrentPointCapabilityLabelWidth(labelText),
      graphWidth,
      graphHeight,
      graphContainer: null,
    })
  )
  // The label must occupy its final slot on the very first painted frame:
  // starting in a default slot and re-placing on the settle timer made it
  // appear above-left — off the graph's left edge whenever the dot starts
  // near it — and visibly slide into place on every HUD open. The lazy
  // initial state above can't see the globe (the container ref isn't
  // attached yet), so re-choose here, pre-paint, with the globe known.
  useLayoutEffect(() => {
    setPlacement(chooseCurrentPointCapabilityLabelPlacement({
      cx,
      cy,
      labelWidth: getCurrentPointCapabilityLabelWidth(labelText),
      graphWidth,
      graphHeight,
      graphContainer: graphContainerRef.current,
    }))
    // Mount only: later dot movement re-evaluates via the settle timer below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // The slot-change transition stays OFF for the whole first frame (a plain
  // effect runs after paint), so the mount-time placement never animates;
  // only slot changes from later dot movement slide.
  const [slotTransitionEnabled, setSlotTransitionEnabled] = useState(false)
  useEffect(() => {
    setSlotTransitionEnabled(true)
  }, [])
  useEffect(() => {
    // Any dot movement restarts the timer, so only the settled position is
    // ever evaluated.
    const settleTimer = setTimeout(() => {
      setPlacement(chooseCurrentPointCapabilityLabelPlacement({
        cx,
        cy,
        labelWidth: getCurrentPointCapabilityLabelWidth(labelText),
        graphWidth,
        graphHeight,
        graphContainer: graphContainerRef.current,
      }))
    }, CURRENT_POINT_CAPABILITY_LABEL_SETTLE_MS)
    return () => clearTimeout(settleTimer)
  }, [cx, cy, labelText, graphWidth, graphHeight, graphContainerRef])
  return (
    <div
      className="absolute pointer-events-none"
      style={{ left: cx, top: cy, zIndex: CURRENT_POINT_MARKER_Z_INDEX }}
    >
      <div
        className="absolute left-0 top-0 font-mono font-bold whitespace-nowrap"
        style={{
          transform: `translate(${
            placement.horizontal === 'left'
              ? `calc(-100% - ${CURRENT_POINT_CAPABILITY_LABEL_GAP_X_PX}px)`
              : placement.horizontal === 'center'
                ? '-50%'
                : `${CURRENT_POINT_CAPABILITY_LABEL_GAP_X_PX}px`
          }, ${
            placement.vertical === 'above'
              ? `calc(-100% - ${CURRENT_POINT_CAPABILITY_LABEL_GAP_Y_PX}px)`
              : `${CURRENT_POINT_CAPABILITY_LABEL_GAP_Y_PX}px`
          })`,
          padding: CURRENT_POINT_CAPABILITY_LABEL_PADDING,
          borderRadius: CURRENT_POINT_CAPABILITY_LABEL_BORDER_RADIUS_PX,
          background: CURRENT_POINT_CAPABILITY_LABEL_BACKGROUND,
          color: CURRENT_POINT_CAPABILITY_LABEL_TEXT_COLOR,
          fontSize: CURRENT_POINT_CAPABILITY_LABEL_FONT_SIZE_PX,
          transition:
            (slotTransitionEnabled
              ? 'transform 350ms cubic-bezier(0.4, 0, 0.2, 1), '
              : '')
            + 'background-color var(--playbook-epilogue-bg-fade-ms, 333ms) cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {labelText}
      </div>
    </div>
  )
}

const getInterpolatedHoverPoint = (
  hoverX: number,
  sortedHoverPoints: ExtrapolatedGraphHoverPoint[]
): ExtrapolatedGraphHoverPoint | null => {
  if (sortedHoverPoints.length === 0) return null
  if (sortedHoverPoints.length === 1) return sortedHoverPoints[0]
  const firstPoint = sortedHoverPoints[0]
  const lastPoint = sortedHoverPoints[sortedHoverPoints.length - 1]
  if (hoverX <= firstPoint.cx) return firstPoint
  if (hoverX >= lastPoint.cx) return lastPoint
  for (let pointIndex = 1; pointIndex < sortedHoverPoints.length; pointIndex += 1) {
    const previousPoint = sortedHoverPoints[pointIndex - 1]
    const nextPoint = sortedHoverPoints[pointIndex]
    if (hoverX > nextPoint.cx) continue
    const pointSpan = nextPoint.cx - previousPoint.cx
    const progressWithinSpan = pointSpan === 0
      ? 0
      : (hoverX - previousPoint.cx) / pointSpan
    return {
      timestamp: previousPoint.timestamp + (
        (nextPoint.timestamp - previousPoint.timestamp) * progressWithinSpan
      ),
      value: previousPoint.value + ((nextPoint.value - previousPoint.value) * progressWithinSpan),
      cx: hoverX,
      cy: previousPoint.cy + ((nextPoint.cy - previousPoint.cy) * progressWithinSpan),
      riskOpacity: previousPoint.riskOpacity + (
        (nextPoint.riskOpacity - previousPoint.riskOpacity) * progressWithinSpan
      ),
    }
  }
  return lastPoint
}

export const ExtrapolatedGraphRenderer = ({
  width,
  height,
  activePlan,
  textColor,
  backgroundColor,
  title,
  graphLabel,
  currentValueLabel,
  currentValueLabelPosition,
  lossOfControlGradientStrip,
  redAboveLossOfControlCanvasLayer,
  blackBelowTooSlowRiskCanvasLayer,
  clipLinesToDitherTop,
  lineSegments,
  futurePointMarkers,
  pastPointMarkers,
  currentPointMarkers,
  annotationMarkers,
  bottomAxisLabels,
  annotations,
  hoverRevealAnnotations,
  hoverPoints,
  onHoveredYearChange,
  pointTooltipDetailsHint,
  pointHoverMaxDistanceToLinePx,
  takeoverRiskAreaHover = false,
  riskFoomPreview,
  riskFoomDesign = "stock",
  riskFoomTedCaption = false,
  riskFoomOnPointHover = false,
  riskSpaceValueAtY,
  riskSpaceTimelineGuide = false,
  riskSpaceAboveGlobe = false,
  riskTedGuideYForValue,
  riskTedGuideBelowGlobe = false,
  riskTedGuideSubtleLine = false,
  riskAmbient,
  riskHoverDimDisabled = false,
  riskZoneDimTween = false,
  riskZoneDimDotShrink = false,
  riskZoneDimInverted = false,
  riskZoneContinuousGuide = false,
  pointGuideTedDash = false,
  highlightLineWhilePointHovered = false,
  lossOfControlHoverReveal = false,
  showAllYearDotTooltips = false,
  editorDotsOverlay,
  safeZoneOverlay,
  currentYearPillYear,
  currentYearPillMonthIndex,
  currentYearPillFlushLeft = false,
  currentYearPillAnchorY,
  onCurrentYearPillClick,
  currentYearPillMenuOpen = false,
  showCurrentPointCapabilityLabel = false,
}: ExtrapolatedGraphRenderProps) => {
  const { setPointTooltip, setTakeoverRiskAreaHover, setRiskAreaHoverYear, setRiskSpaceReadout, setRiskTedGuide, setRiskSpaceGuide, hasChannel } = useBandHudOverlayPublisher()
  const pillDropProgress = usePillDropProgress(currentYearPillFlushLeft)
  const [hoveredYearKey, setHoveredYearKey] = useState<string | null>(null)
  const [isGraphHovered, setIsGraphHovered] = useState(false)
  const [hoverX, setHoverX] = useState<number | null>(null)
  // Tracked alongside hoverX for the pointHoverMaxDistanceToLinePx gate —
  // graph-local, unclamped (only ever compared against the line's cy).
  const [hoverY, setHoverY] = useState<number | null>(null)
  const graphContainerRef = useRef<HTMLDivElement | null>(null)
  const gradientStripClipId = useId()
  const graphCanvasPixelRatio = useGraphCanvasPixelRatio()
  const updateHoverXFromClientX = useCallback(
    (clientX: number, clientY?: number) => {
      const graphContainer = graphContainerRef.current
      if (!graphContainer) return
      const rect = graphContainer.getBoundingClientRect()
      setHoverX(Math.max(0, Math.min(width, clientX - rect.left)))
      setHoverY(clientY === undefined ? null : clientY - rect.top)
    },
    [width],
  )
  const getMarkerRenderKey = (
    markerKey: string | undefined,
    cx: number,
    cy: number,
    seriesPrefix: string
  ) =>
    markerKey ?? `${seriesPrefix}-point-marker-${cx}-${cy}`

  const sortedHoverPoints = useMemo(
    () => [...(hoverPoints ?? [])].sort((pointA, pointB) => pointA.cx - pointB.cx),
    [hoverPoints],
  )
  const allYearMarkers = useMemo(
    () => [
      ...futurePointMarkers.map(marker => ({
        renderKey: getMarkerRenderKey(marker.key, marker.cx, marker.cy, 'future'),
        date: marker.date,
        timestamp: marker.timestamp,
        value: marker.value,
        cx: marker.cx,
        cy: marker.cy,
        r: marker.r ?? CAPABILITIES_FUTURE_DOT_RADIUS,
      })),
      ...pastPointMarkers.map(marker => ({
        renderKey: getMarkerRenderKey(marker.key, marker.cx, marker.cy, 'past'),
        date: marker.date,
        timestamp: marker.timestamp,
        value: marker.value,
        cx: marker.cx,
        cy: marker.cy,
        r: marker.r ?? CAPABILITIES_PAST_DOT_RADIUS,
      })),
      ...currentPointMarkers.map(marker => ({
        renderKey: getMarkerRenderKey(marker.key, marker.cx, marker.cy, 'current'),
        date: marker.date,
        timestamp: marker.timestamp,
        value: marker.value,
        cx: marker.cx,
        cy: marker.cy,
        r: marker.r ?? CAPABILITIES_CURRENT_DOT_RADIUS,
      })),
    ],
    [futurePointMarkers, pastPointMarkers, currentPointMarkers],
  )
  const getCapabilityThresholdLabel = (value: number) => getCapabilityStageLabelForPlanValue(value, activePlan)
  // The pointer is in the takeover-risk field ABOVE the line (past the
  // landing strip). Splits the graph box between the two hovers: the point
  // hover keeps everything on/below the line, as before.
  const isTakeoverRiskAreaHovered = useMemo(() => {
    if (!takeoverRiskAreaHover || !isGraphHovered || hoverX === null || hoverY === null) {
      return false
    }
    const interpolatedPoint = getInterpolatedHoverPoint(hoverX, sortedHoverPoints)
    if (!interpolatedPoint) return false
    return hoverY < interpolatedPoint.cy - TAKEOVER_RISK_AREA_LINE_CLEARANCE_PX
  }, [takeoverRiskAreaHover, isGraphHovered, hoverX, hoverY, sortedHoverPoints])
  const hoveredGraphPoint = useMemo(() => {
    if (!isGraphHovered || hoverX === null) return null
    // The risk field above the line is its own hover area — see
    // takeoverRiskAreaHover in types.ts.
    if (isTakeoverRiskAreaHovered) return null
    const interpolatedPoint = getInterpolatedHoverPoint(hoverX, sortedHoverPoints)
    if (!interpolatedPoint) return null
    // Line-proximity gate — see pointHoverMaxDistanceToLinePx in types.ts.
    if (
      pointHoverMaxDistanceToLinePx !== undefined &&
      (hoverY === null ||
        Math.abs(hoverY - interpolatedPoint.cy) > pointHoverMaxDistanceToLinePx)
    ) {
      return null
    }
    const riskBucketIndex = getRiskBucketIndex(interpolatedPoint.riskOpacity)
    return {
      ...interpolatedPoint,
      yearText: `${new Date(interpolatedPoint.timestamp).getUTCFullYear()}`,
      speedupText: formatSpeedupHoverText(interpolatedPoint.value),
      capabilityThresholdLabel: getCapabilityThresholdLabel(interpolatedPoint.value),
      capabilityThresholdRiskLabel: getRiskLabelForComputeValue(interpolatedPoint.value),
      riskLevelLabel: getRiskBucketLabel(interpolatedPoint.riskOpacity),
      riskBucketIndex,
      isModerateOrHigherRisk: getIsModerateOrHigherRisk(interpolatedPoint.riskOpacity),
      isExtremeRisk: riskBucketIndex === EXTREME_RISK_BUCKET_INDEX,
    }
  }, [isGraphHovered, hoverX, hoverY, sortedHoverPoints, pointHoverMaxDistanceToLinePx, isTakeoverRiskAreaHovered])

  const hoveredPointYear = hoveredGraphPoint
    ? Number.parseInt(hoveredGraphPoint.yearText, 10)
    : null

  useEffect(() => {
    onHoveredYearChange?.(hoveredPointYear)
  }, [hoveredPointYear, onHoveredYearChange])
  type InteractiveYearTooltipTarget = {
    key: string
    yearText: string
    speedupText: string
    capabilityThresholdLabel: string
    labelX?: number
    labelY?: number
    marker?: (typeof allYearMarkers)[number]
  }
  const interactiveYears = useMemo(
    () => {
      const getInteractiveYearForMarker = (
        marker: (typeof allYearMarkers)[number],
        key: string,
        labelX?: number,
        labelY?: number
      ): InteractiveYearTooltipTarget => {
        const speedupValue = marker.value
        const yearText = marker.timestamp !== undefined
          ? `${new Date(marker.timestamp).getUTCFullYear()}`
          : 'Unknown'
        return {
          key,
          yearText,
          speedupText: speedupValue !== undefined
            ? formatSpeedupHoverText(speedupValue)
            : 'Unknown',
          capabilityThresholdLabel: speedupValue !== undefined
            ? getCapabilityThresholdLabel(speedupValue)
            : 'Unknown',
          labelX,
          labelY,
          marker,
        }
      }
      const labeledYearTargets = bottomAxisLabels.map((label, labelIndex) => {
        const labelYear = Number.parseInt(label.text, 10)
        let closestMarkerForYear: (typeof allYearMarkers)[number] | undefined
        let closestMarkerForYearDelta = Number.POSITIVE_INFINITY
        let closestMarkerByX: (typeof allYearMarkers)[number] | undefined
        let closestMarkerByXDelta = Number.POSITIVE_INFINITY
        for (const marker of allYearMarkers) {
          const markerYear = marker.timestamp !== undefined
            ? new Date(marker.timestamp).getUTCFullYear()
            : undefined
          const deltaX = Math.abs(marker.cx - label.x)
          if (deltaX < closestMarkerByXDelta) {
            closestMarkerByX = marker
            closestMarkerByXDelta = deltaX
          }
          if (markerYear === labelYear && deltaX < closestMarkerForYearDelta) {
            closestMarkerForYear = marker
            closestMarkerForYearDelta = deltaX
          }
        }
        const markerForYear = closestMarkerForYear ?? (
          closestMarkerByXDelta <= 24
            ? closestMarkerByX
            : undefined
        )
        const speedupValue = markerForYear?.value
        return {
          key: `year-${label.key}-${labelIndex}`,
          yearText: label.text,
          speedupText: speedupValue !== undefined
            ? formatSpeedupHoverText(speedupValue)
            : 'Unknown',
          capabilityThresholdLabel: speedupValue !== undefined
            ? getCapabilityThresholdLabel(speedupValue)
            : 'Unknown',
          labelX: label.x,
          labelY: height - 2,
          marker: markerForYear,
        }
      })
      if (!showAllYearDotTooltips) return labeledYearTargets
      const labeledMarkerKeys = new Set(
        labeledYearTargets.flatMap(year => year.marker ? [year.marker.renderKey] : [])
      )
      const unlabeledYearMarkers = allYearMarkers.filter(marker =>
        !labeledMarkerKeys.has(marker.renderKey)
      )
      const unlabeledYearTargets = unlabeledYearMarkers.map(marker =>
        getInteractiveYearForMarker(marker, `year-dot-${marker.renderKey}`)
      )
      return [...labeledYearTargets, ...unlabeledYearTargets]
    },
    [bottomAxisLabels, allYearMarkers, height, showAllYearDotTooltips],
  )
  const markerToYearMap = useMemo(
    () => interactiveYears.reduce<Record<string, string>>((map, year) => {
      if (!year.marker) return map
      map[year.marker.renderKey] = year.key
      return map
    }, {}),
    [interactiveYears],
  )
  const getMarkerFilter = (saturation: number | undefined, markerRenderKey: string) => {
    const filterParts = []
    if (saturation !== undefined) filterParts.push(`saturate(${saturation})`)
    if (hoveredYearKey && markerToYearMap[markerRenderKey] === hoveredYearKey) {
      filterParts.push('brightness(.5)')
    }
    return filterParts.length > 0 ? filterParts.join(' ') : undefined
  }
  const currentValueLabelStroke = getLabelStroke(backgroundColor)
  const annotationLabelLayer = (
    <>
      {annotations?.map((annotation, annotationIndex) => (
        <AnnotationLabel
          key={annotation.key}
          annotation={annotation}
          backgroundColor={backgroundColor}
          isHighestVisible={
            annotationIndex === annotations.findLastIndex(
              a => (a.opacity ?? 1) > 0
            )
          }
        />
      ))}
      {hoverRevealAnnotations?.map(hoverRevealAnnotation => {
        const isHoverRevealVisible = isGraphHovered || lossOfControlHoverReveal
        return (
          <AnnotationLabel
            key={hoverRevealAnnotation.key}
            annotation={{
              ...hoverRevealAnnotation,
              opacity: isHoverRevealVisible ? (hoverRevealAnnotation.opacity ?? 1) : 0,
              pointerEvents: isHoverRevealVisible ? hoverRevealAnnotation.pointerEvents : 'none',
            }}
            backgroundColor={backgroundColor}
            isHighestVisible={false}
          />
        )
      })}
    </>
  )
  // The (fractional) year under the pointer while the risk field is hovered
  // — interpolated along the capability hover points' timestamps at hoverX,
  // so the cursor drives the risk takeover's "current year" the same way the
  // point hover drives the capability ladder. Also the foom preview's anchor.
  const hoveredRiskAreaPoint = useMemo(() => {
    // The combined hover (riskFoomOnPointHover): the WHOLE graph is one
    // hover — the point hover keeps its dot/ladder everywhere, and the foom
    // preview + cursor year ride along from the same interpolated point.
    const combinedHoverEngaged = riskFoomOnPointHover && isGraphHovered
    if (!(isTakeoverRiskAreaHovered || combinedHoverEngaged) || hoverX === null) return null
    const interpolatedPoint = getInterpolatedHoverPoint(hoverX, sortedHoverPoints)
    if (!interpolatedPoint) return null
    const date = new Date(interpolatedPoint.timestamp)
    const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1)
    const nextYearStart = Date.UTC(date.getUTCFullYear() + 1, 0, 1)
    const year =
      date.getUTCFullYear() +
      (interpolatedPoint.timestamp - yearStart) / (nextYearStart - yearStart)
    // The line's screen slope (dy/dx) at the takeoff point, sampled a short
    // step ahead (behind at the right edge) — the foom preview's tangent
    // designs leave along it.
    const SLOPE_SAMPLE_DX_PX = 12
    const ahead = getInterpolatedHoverPoint(hoverX + SLOPE_SAMPLE_DX_PX, sortedHoverPoints)
    const behind = getInterpolatedHoverPoint(hoverX - SLOPE_SAMPLE_DX_PX, sortedHoverPoints)
    const slope =
      ahead && ahead.cx > interpolatedPoint.cx
        ? (ahead.cy - interpolatedPoint.cy) / (ahead.cx - interpolatedPoint.cx)
        : behind && behind.cx < interpolatedPoint.cx
          ? (interpolatedPoint.cy - behind.cy) / (interpolatedPoint.cx - behind.cx)
          : 0
    return { year, cx: interpolatedPoint.cx, cy: interpolatedPoint.cy, slope }
  }, [isTakeoverRiskAreaHovered, riskFoomOnPointHover, isGraphHovered, hoverX, sortedHoverPoints])
  // The graph's x scale in px per year, from the hover points' span — the
  // foom preview's risk-over-time layers map the likelihood table onto the
  // graph's own year axis with it.
  const pxPerYear = useMemo(() => {
    if (sortedHoverPoints.length < 2) return null
    const first = sortedHoverPoints[0]
    const last = sortedHoverPoints[sortedHoverPoints.length - 1]
    const yearSpan = (last.timestamp - first.timestamp) / (365.25 * 24 * 3600 * 1000)
    if (yearSpan <= 0 || last.cx <= first.cx) return null
    return (last.cx - first.cx) / yearSpan
  }, [sortedHoverPoints])
  // While the risk field is hovered — or, on the combined hover, while the
  // foom preview is up anywhere on the graph — the whole field lightens.
  // A new layer object so the canvas redraw effect picks the change up.
  // riskZoneDimInverted (tab 70) flips the semantics: the field lightens
  // while the LINE zone is hovered and keeps full ink while it is itself —
  // the zone under the pointer stays saturated.
  const riskFieldLightened =
    !riskHoverDimDisabled &&
    (riskZoneDimInverted
      ? hoveredGraphPoint !== null
      : isTakeoverRiskAreaHovered ||
        (riskFoomOnPointHover && hoveredRiskAreaPoint !== null))
  // With riskZoneDimTween the factor EASES between 1 and the hover strength
  // (RISK_ZONE_DIM_TWEEN_MS); without it, it snaps like before. The
  // dot-shrink mode replaces the opacity dim entirely, so its factor stays 1.
  const riskFieldDimFactor = useTweenedNumber(
    riskFieldLightened && !riskZoneDimDotShrink
      ? TAKEOVER_RISK_AREA_HOVER_OPACITY
      : 1,
    RISK_ZONE_DIM_TWEEN_MS,
    riskZoneDimTween,
  )
  // The dot-shrink dim (riskZoneDimDotShrink): the field's response is the
  // dots' RADII easing down to RISK_ZONE_DOT_SHRINK_RADIUS_SCALE instead of
  // the layer losing opacity — full ink, visibly thinner field.
  const riskFieldDotRadiusScale = useTweenedNumber(
    riskFieldLightened && riskZoneDimDotShrink
      ? RISK_ZONE_DOT_SHRINK_RADIUS_SCALE
      : 1,
    RISK_ZONE_DIM_TWEEN_MS,
    riskZoneDimTween,
  )
  // The line-hover stroke highlight rides the same clock as the field dim
  // when riskZoneDimTween is on (tab 67), so the two zones' canvas responses
  // ease in concert instead of snapping.
  const lineHighlightExtraStrokePx = useTweenedNumber(
    highlightLineWhilePointHovered && hoveredGraphPoint
      ? LINE_HOVER_HIGHLIGHT_EXTRA_STROKE_PX
      : 0,
    RISK_ZONE_DIM_TWEEN_MS,
    riskZoneDimTween,
  )
  const displayedRedAboveLossOfControlCanvasLayer = useMemo(() => {
    if (!redAboveLossOfControlCanvasLayer) return redAboveLossOfControlCanvasLayer
    if (riskFieldDotRadiusScale < 1) {
      const base = redAboveLossOfControlCanvasLayer.thicknessAdjustment
      return {
        ...redAboveLossOfControlCanvasLayer,
        thicknessAdjustment: {
          dotRadiusMultiplier: base?.dotRadiusMultiplier ?? 1,
          dotRadiusAdditivePx: base?.dotRadiusAdditivePx ?? 0,
          dotRadiusScale: riskFieldDotRadiusScale,
        },
      }
    }
    if (riskFieldDimFactor < 1) {
      return {
        ...redAboveLossOfControlCanvasLayer,
        opacity: redAboveLossOfControlCanvasLayer.opacity * riskFieldDimFactor,
      }
    }
    return redAboveLossOfControlCanvasLayer
  }, [redAboveLossOfControlCanvasLayer, riskFieldDimFactor, riskFieldDotRadiusScale])
  // clipLinesToDitherTop (types.ts): the red dither field is 'top'-anchored,
  // so its dots always run to the CANVAS TOP — the field's visible top edge
  // is the canvas edge itself (y=0), and the canvas raster already crops the
  // line strokes there. This boundary exists for the DOM-rendered overlays
  // (point-marker dots, the hover dot + guide line, the current-point label),
  // which overflow the canvas box freely and used to float into the page nav.
  // An earlier take clipped beneath the field's loss-of-control CURVES —
  // wrong on both ends: mid-story the curves run thousands of px off-plot
  // above the canvas (overlay dots at negative y never matched), and where
  // they come on-plot they are the field's lower density fade, so the
  // polyline clip cut the line mid-field (the white channel through the
  // dither on Plan D at Jan 2032).
  const lineClipBoundaryYAtX = useCallback((_cx: number): number | null => {
    if (!clipLinesToDitherTop) return null
    if (!displayedRedAboveLossOfControlCanvasLayer) return null
    return 0
  }, [clipLinesToDitherTop, displayedRedAboveLossOfControlCanvasLayer])
  // Marker counterpart of the line clip: the line's point DOTS are separate
  // SVG circles (not on the raster-cropped canvas), so any marker whose
  // center sits above the dither's top boundary is hidden with the line.
  const isAboveLineClipBoundary = (cx: number, cy: number): boolean => {
    const boundaryY = lineClipBoundaryYAtX(cx)
    return boundaryY !== null && cy < boundaryY
  }
  const hoverGuideLineBottom = height - 14
  // The point-on-line hover tooltip (box + dot + guide line). All coordinates
  // are graph-local. When an above-globe layer is present (the HUD globe/graph
  // pairing), we publish this so the tooltip paints over the globe in scroll
  // lockstep; otherwise (side panel, editor previews — no globe) we render it
  // in-flow here as before. The transparent hover hitboxes (YearPointTooltip)
  // always stay in-flow below — they only catch the mouse, not get seen.
  const pointTooltipPayload = useMemo<PointHoverTooltipPayload | null>(
    () => {
      if (!hoveredGraphPoint) return null
      // With the dither clip on, a hovered point above the field's top edge
      // has no visible line under it — hide the on-line dot there and start
      // the dotted guide line at the field's edge instead, so nothing of the
      // hover paints above the dither (tab 49's clipLinesToDitherTop).
      const clipBoundaryY = lineClipBoundaryYAtX(hoveredGraphPoint.cx)
      const dotHidden = clipBoundaryY !== null && hoveredGraphPoint.cy < clipBoundaryY
      const guideLineTop = dotHidden ? clipBoundaryY : hoveredGraphPoint.cy + 4
      return {
        cx: hoveredGraphPoint.cx,
        cy: hoveredGraphPoint.cy,
        year: Number.parseInt(hoveredGraphPoint.yearText, 10),
        capabilityThresholdLabel: hoveredGraphPoint.capabilityThresholdLabel,
        speedupText: hoveredGraphPoint.speedupText,
        riskLabel: hoveredGraphPoint.capabilityThresholdRiskLabel,
        riskFraction: hoveredGraphPoint.riskOpacity,
        capabilityValue: hoveredGraphPoint.value,
        textColor,
        guideLineTop,
        // The continuous zone guide (riskZoneContinuousGuide) draws the
        // cursor column and on-line dot itself, in the graph body — zero the
        // tooltip's own guide and dot so they don't double up above the globe.
        guideLineHeight: riskZoneContinuousGuide
          ? 0
          : Math.max(0, hoverGuideLineBottom - guideLineTop),
        dotHidden: dotHidden || riskZoneContinuousGuide,
        guideTedDash: pointGuideTedDash,
        detailsHint: pointTooltipDetailsHint,
      }
    },
    [hoveredGraphPoint, textColor, hoverGuideLineBottom, lineClipBoundaryYAtX, pointTooltipDetailsHint, riskZoneContinuousGuide, pointGuideTedDash],
  )
  useEffect(() => {
    if (!hasChannel) return
    setPointTooltip(pointTooltipPayload)
    return () => setPointTooltip(null)
  }, [hasChannel, pointTooltipPayload, setPointTooltip])
  useEffect(() => {
    if (!hasChannel) return
    setTakeoverRiskAreaHover(isTakeoverRiskAreaHovered)
    return () => setTakeoverRiskAreaHover(false)
  }, [hasChannel, isTakeoverRiskAreaHovered, setTakeoverRiskAreaHover])
  const hoveredRiskAreaYear = hoveredRiskAreaPoint?.year ?? null
  useEffect(() => {
    if (!hasChannel) return
    setRiskAreaHoverYear(hoveredRiskAreaYear)
    return () => setRiskAreaHoverYear(null)
  }, [hasChannel, hoveredRiskAreaYear, setRiskAreaHoverYear])
  // The 2D risk-space explorer's readout (riskSpaceValueAtY set): the
  // capability level at the cursor's HEIGHT, and Joanna's authored risk for
  // reaching that level in the cursor's year (currentLevelRiskAt — NOT the
  // field's pixel density, which this HUD stylizes). Drawn in-canvas by
  // RiskSpaceExplorerOverlay and published for the HUD's takeover panel.
  const riskSpaceReadout = useMemo(() => {
    if (
      !riskSpaceValueAtY ||
      !isTakeoverRiskAreaHovered ||
      !hoveredRiskAreaPoint ||
      hoverX === null ||
      hoverY === null
    ) {
      return null
    }
    const capabilityValue = riskSpaceValueAtY(hoverY)
    const riskFraction = currentLevelRiskAt(
      capabilityValue,
      activePlan,
      hoveredRiskAreaPoint.year,
    )
    if (riskFraction === null) return null
    return {
      x: hoverX,
      y: hoverY,
      year: Math.floor(hoveredRiskAreaPoint.year),
      levelLabel: getCapabilityStageLabelForPlanValue(capabilityValue, activePlan),
      riskFraction,
    }
  }, [riskSpaceValueAtY, isTakeoverRiskAreaHovered, hoveredRiskAreaPoint, hoverX, hoverY, activePlan])
  const publishedRiskSpaceReadout = useMemo(
    () =>
      riskSpaceReadout
        ? { levelLabel: riskSpaceReadout.levelLabel, riskFraction: riskSpaceReadout.riskFraction }
        : null,
    [riskSpaceReadout],
  )
  useEffect(() => {
    if (!hasChannel) return
    setRiskSpaceReadout(publishedRiskSpaceReadout)
    return () => setRiskSpaceReadout(null)
  }, [hasChannel, publishedRiskSpaceReadout, setRiskSpaceReadout])
  // The above-globe explorer (riskSpaceAboveGlobe, picks tab 6): the whole
  // square/readout/guide payload publishes to the above-globe layer, where
  // AboveGlobeBandHudOverlay renders it on top of the earth; the in-body
  // render below is skipped.
  const riskSpaceGuidePayload = useMemo(
    () =>
      riskSpaceAboveGlobe && riskSpaceReadout
        ? { ...riskSpaceReadout, width, height }
        : null,
    [riskSpaceAboveGlobe, riskSpaceReadout, width, height],
  )
  useEffect(() => {
    if (!hasChannel) return
    setRiskSpaceGuide(riskSpaceGuidePayload)
    return () => setRiskSpaceGuide(null)
  }, [hasChannel, riskSpaceGuidePayload, setRiskSpaceGuide])
  // The TED guide's payload (riskTedGuideYForValue set) — published so the
  // guide paints in the ABOVE-GLOBE layer: the line and label sit on top of
  // the earth when the cursor's column passes behind it. (Hovering the earth
  // itself ends the graph hover via the pointer gate, so the guide never
  // fights the datacenter hover.) Channel-less graphs render it in-flow.
  const riskTedGuidePayload = useMemo(() => {
    if (!riskTedGuideYForValue || !isTakeoverRiskAreaHovered || !hoveredRiskAreaPoint) {
      return null
    }
    const tedValue =
      CAPABILITY_STAGE_LADDERS[capabilityStageScaleForPlan(activePlan)].find(
        (stage) => stage.short === 'TED',
      )?.atOrAbove ?? Number.POSITIVE_INFINITY
    return {
      x: hoveredRiskAreaPoint.cx,
      tedY: riskTedGuideYForValue(tedValue),
      year: Math.floor(hoveredRiskAreaPoint.year),
      width,
      height,
    }
  }, [riskTedGuideYForValue, isTakeoverRiskAreaHovered, hoveredRiskAreaPoint, activePlan, width, height])
  useEffect(() => {
    // "below-earth" keeps the guide in the graph body — don't publish. The
    // continuous zone guide (riskZoneContinuousGuide) replaces the TED guide
    // outright, so it never publishes either.
    if (!hasChannel || riskTedGuideBelowGlobe || riskZoneContinuousGuide) return
    setRiskTedGuide(riskTedGuidePayload)
    return () => setRiskTedGuide(null)
  }, [hasChannel, riskTedGuideBelowGlobe, riskZoneContinuousGuide, riskTedGuidePayload, setRiskTedGuide])
  // The continuous zone guide's payload (riskZoneContinuousGuide, tab 68):
  // ONE cursor column serving both hover zones — black axis-to-line while
  // the line zone is hovered, red axis-to-frame-top while the field is.
  // Rendered in the graph body (below the globe, like the below-earth TED
  // guide) by RiskZoneContinuousGuideOverlay, which CSS-transitions the
  // column between the two states so crossing the boundary reads as one
  // morph.
  const riskZoneGuidePayload = useMemo(() => {
    if (!riskZoneContinuousGuide) return null
    const anchor = hoveredGraphPoint ?? hoveredRiskAreaPoint
    if (!anchor) return null
    return {
      mode: hoveredGraphPoint ? ('line' as const) : ('field' as const),
      x: anchor.cx,
      lineY: anchor.cy,
      year: hoveredGraphPoint
        ? Number.parseInt(hoveredGraphPoint.yearText, 10)
        : Math.floor(hoveredRiskAreaPoint?.year ?? 0),
      width,
      height,
    }
  }, [riskZoneContinuousGuide, hoveredGraphPoint, hoveredRiskAreaPoint, width, height])
  const labelLayerContent = (
    <>
      {annotationLabelLayer}
      {!hasChannel && pointTooltipPayload && (
        <GraphPointHoverTooltip point={pointTooltipPayload} />
      )}
      {editorDotsOverlay}
    </>
  )
  return (
    <div className="relative pointer-events-none">
      {title && (
        <span className="text-[10px] absolute top-0 left-0 pointer-events-none font-bold">{title}</span>
      )}
      {graphLabel && (
        <span className={`text-[10px] absolute left-0${title ? ' top-[10px]' : ' top-0'}`}>
          {graphLabel}
        </span>
      )}
      <div
        ref={graphContainerRef}
        className="mb-1 relative pointer-events-auto"
        // touchAction pan-y lets a vertical swipe scroll the page even when
        // it starts inside the graph (which sits in the fixed mobile preview
        // and re-enables pointer events for hover tracking). The globe canvas
        // above is pointer-events: none on touch devices, so touches fall
        // through to this div — without pan-y, the page can't scroll once
        // the gesture begins anywhere over the graph or globe area.
        style={{ width, color: textColor, zIndex: 0, touchAction: 'pan-y' }}
        onMouseEnter={() => setIsGraphHovered(true)}
        onMouseMove={event => updateHoverXFromClientX(event.clientX, event.clientY)}
        onMouseLeave={() => {
          setIsGraphHovered(false)
          setHoverX(null)
          setHoverY(null)
        }}
        onTouchStart={event => {
          setIsGraphHovered(true)
          updateHoverXFromClientX(
            event.touches[0]?.clientX ?? 0,
            event.touches[0]?.clientY,
          )
        }}
        onTouchMove={event => {
          setIsGraphHovered(true)
          updateHoverXFromClientX(
            event.touches[0]?.clientX ?? 0,
            event.touches[0]?.clientY,
          )
        }}
      >
        <svg
          width={width}
          height={height}
          style={{ position: 'relative', zIndex: 2, overflow: 'visible' }}
        >
          <defs>
            {/* The gradient strip traces the loss-of-control curves, which can
                run thousands of px off-plot on the log scale. The svg keeps
                overflow visible (year pill, labels), so without this clip the
                strip inflates the svg's composited layer to the full path
                bounds — a ~7,000px-tall GPU layer repainted on every scroll
                frame. Clip to the plot rect plus a margin for the intended
                overhang. */}
            <clipPath id={gradientStripClipId}>
              <rect x={-60} y={-60} width={width + 120} height={height + 120} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${gradientStripClipId})`}>
            {lossOfControlGradientStrip}
          </g>
          {annotationMarkers?.map(marker => (
            <g key={marker.key}>
              <circle
                cx={marker.cx}
                cy={marker.cy}
                r={marker.r ?? 1.6}
                fill={marker.fill ?? backgroundColor}
                stroke={marker.stroke}
                strokeWidth={marker.strokeWidth}
                fillOpacity={marker.fillOpacity}
                style={{
                  filter: marker.saturation !== undefined
                    ? `saturate(${marker.saturation})`
                    : undefined,
                }}
              />
              {marker.innerFill && (
                <circle
                  cx={marker.cx}
                  cy={marker.cy}
                  r={marker.innerR ?? 0.75}
                  fill={marker.innerFill}
                  fillOpacity={marker.innerFillOpacity}
                />
              )}
            </g>
          ))}
          {bottomAxisLabels.map(label => (
            <text
              key={label.key}
              x={label.x}
              // All year labels share the 14px size now, so they sit on a
              // single baseline (the current/hovered year stays darker via fill).
              y={height - 1}
              textAnchor={label.textAnchor}
              fontSize={14}
              fontWeight="normal"
              fill={label.fill ?? textColor}
              opacity={label.opacity}
            >
              {label.text}
            </text>
          ))}
          {/* The continuous zone guide draws the hovered year itself (color-
              transitioning between the zones' inks) — skip the plain black
              one so they don't stack. */}
          {hoveredGraphPoint && !riskZoneContinuousGuide && (
            <text
              x={hoveredGraphPoint.cx}
              y={height - 1}
              textAnchor="middle"
              fontSize="14"
              fill={textColor}
              style={{ textShadow: currentValueLabelStroke }}
            >
              {hoveredGraphPoint.yearText}
            </text>
          )}
        </svg>
        {safeZoneOverlay}
        {(lineSegments.length > 0
          || redAboveLossOfControlCanvasLayer
          || blackBelowTooSlowRiskCanvasLayer) && (
          <GraphCanvasLayer
            width={width}
            height={height}
            lineSegments={lineSegments}
            pixelRatio={graphCanvasPixelRatio}
            redAboveLossOfControlCanvasLayer={displayedRedAboveLossOfControlCanvasLayer}
            blackBelowTooSlowRiskCanvasLayer={blackBelowTooSlowRiskCanvasLayer}
            lineExtraStrokePx={lineHighlightExtraStrokePx}
            dashedLineClipMaxX={
              riskFoomPreview &&
              hoveredRiskAreaPoint &&
              RISK_FOOM_DESIGNS[riskFoomDesign].truncateFutureLine
                ? hoveredRiskAreaPoint.cx
                : undefined
            }
          />
        )}
        {riskFoomPreview && hoveredRiskAreaPoint && (
          <RiskFoomPreviewOverlay
            originX={hoveredRiskAreaPoint.cx}
            originY={hoveredRiskAreaPoint.cy}
            originSlope={hoveredRiskAreaPoint.slope}
            hoveredYear={hoveredRiskAreaPoint.year}
            width={width}
            height={height}
            mode={riskFoomPreview}
            design={riskFoomDesign}
            tedCaption={riskFoomTedCaption}
            pxPerYear={pxPerYear}
          />
        )}
        {(!hasChannel || riskTedGuideBelowGlobe) && !riskZoneContinuousGuide && riskTedGuidePayload && (
          <RiskTedGuideOverlay
            {...riskTedGuidePayload}
            subtleLine={riskTedGuideSubtleLine}
            // The tab-34 family (the below-earth modes): the line runs the
            // full frame height, never stopping at the TED level.
            fullHeight={riskTedGuideBelowGlobe}
          />
        )}
        {riskZoneGuidePayload && (
          <RiskZoneContinuousGuideOverlay {...riskZoneGuidePayload} />
        )}
        {riskAmbient && (
          <RiskAmbientOverlay
            design={riskAmbient.design}
            fraction={riskAmbient.fraction}
            currentX={currentValueLabelPosition?.x ?? null}
            currentY={currentValueLabelPosition?.y ?? null}
            width={width}
            height={height}
          />
        )}
        {/* The above-globe mode renders via the published payload in
            AboveGlobeBandHudOverlay instead — skip the in-body copy. */}
        {riskSpaceReadout && !riskSpaceAboveGlobe && (
          <RiskSpaceExplorerOverlay
            x={riskSpaceReadout.x}
            y={riskSpaceReadout.y}
            levelLabel={riskSpaceReadout.levelLabel}
            riskFraction={riskSpaceReadout.riskFraction}
            year={riskSpaceReadout.year}
            width={width}
            height={height}
            timelineGuide={riskSpaceTimelineGuide}
          />
        )}
        {futurePointMarkers.map(marker => {
          if (isAboveLineClipBoundary(marker.cx, marker.cy)) return null
          const renderKey = getMarkerRenderKey(
            marker.key, marker.cx, marker.cy, 'future'
          )
          return (
            <PointMarkerCircle
              key={renderKey}
              cx={marker.cx}
              cy={marker.cy}
              r={marker.r}
              fill={marker.fill}
              stroke={marker.stroke}
              strokeWidth={marker.strokeWidth}
              fillOpacity={marker.fillOpacity}
              defaultFill={backgroundColor}
              defaultR={CAPABILITIES_FUTURE_DOT_RADIUS}
              filter={getMarkerFilter(marker.saturation, renderKey)}
              backgroundColor={backgroundColor}
            />
          )
        })}
        {pastPointMarkers.map(marker => {
          if (isAboveLineClipBoundary(marker.cx, marker.cy)) return null
          const renderKey = getMarkerRenderKey(
            marker.key, marker.cx, marker.cy, 'past'
          )
          return (
            <PointMarkerCircle
              key={renderKey}
              cx={marker.cx}
              cy={marker.cy}
              r={marker.r}
              fill={marker.fill}
              stroke={marker.stroke}
              strokeWidth={marker.strokeWidth}
              fillOpacity={marker.fillOpacity}
              defaultFill={CAPABILITIES_LINE_COLOR}
              defaultR={CAPABILITIES_PAST_DOT_RADIUS}
              filter={getMarkerFilter(marker.saturation, renderKey)}
            />
          )
        })}
        {currentPointMarkers.map(marker => {
          if (isAboveLineClipBoundary(marker.cx, marker.cy)) return null
          const renderKey = getMarkerRenderKey(
            marker.key, marker.cx, marker.cy, 'current'
          )
          return (
            <PointMarkerCircle
              key={renderKey}
              cx={marker.cx}
              cy={marker.cy}
              r={marker.r}
              fill={marker.fill}
              stroke={marker.stroke}
              strokeWidth={marker.strokeWidth}
              fillOpacity={marker.fillOpacity}
              defaultFill={CAPABILITIES_LINE_COLOR}
              defaultR={CAPABILITIES_CURRENT_DOT_RADIUS}
              filter={getMarkerFilter(marker.saturation, renderKey)}
              zIndex={CURRENT_POINT_MARKER_Z_INDEX}
            />
          )
        })}
        {showCurrentPointCapabilityLabel && (() => {
          const currentPointMarker = currentPointMarkers[0]
          if (currentPointMarker?.value === undefined) return null
          // Hidden with its dot when the dot sits above the dither clip.
          if (isAboveLineClipBoundary(currentPointMarker.cx, currentPointMarker.cy)) return null
          return (
            <CurrentPointCapabilityLabel
              cx={currentPointMarker.cx}
              cy={currentPointMarker.cy}
              labelText={getCapabilityThresholdLabel(currentPointMarker.value)}
              graphWidth={width}
              graphHeight={height}
              graphContainerRef={graphContainerRef}
            />
          )
        })()}
        {interactiveYears.map(interactiveYear => (
          <YearPointTooltip
            key={interactiveYear.key}
            tooltipKey={interactiveYear.key}
            yearText={interactiveYear.yearText}
            speedupText={interactiveYear.speedupText}
            capabilityThresholdLabel={interactiveYear.capabilityThresholdLabel}
            labelX={interactiveYear.labelX}
            labelY={interactiveYear.labelY}
            labelHitboxWidth={Math.max(24, interactiveYear.yearText.length * 6)}
            containerWidth={width}
            dotX={interactiveYear.marker?.cx}
            dotY={interactiveYear.marker?.cy}
            dotRadius={interactiveYear.marker?.r}
            onEnter={() => setHoveredYearKey(interactiveYear.key)}
            onLeave={() => setHoveredYearKey(currentHoveredYearKey =>
              currentHoveredYearKey === interactiveYear.key ? null : currentHoveredYearKey
            )}
          />
        ))}
        {currentYearPillYear !== undefined && (() => {
          // Blend the pill between its top-left pin and its epilogue anchor
          // (just above the capabilities line at the current year) by the
          // tweened drop progress — see usePillDropProgress. The anchor is
          // read live each render, so the fully dropped pill rides the line
          // exactly, and a mid-flight reversal blends back from wherever the
          // pill currently is. Bottom of the graph is the fallback anchor
          // (the line's left end rests there in the epilogue viewport).
          const droppedTop = (currentYearPillAnchorY ?? height)
            - CURRENT_YEAR_PILL_EPILOGUE_GAP_ABOVE_LINE_PX
          const droppedLeft = -CURRENT_YEAR_PILL_H_PADDING_PX
          const lerpByDropProgress = (pinned: number, dropped: number) =>
            pinned + (dropped - pinned) * pillDropProgress
          // With onCurrentYearPillClick the pill is the HUD's year-jump menu
          // trigger — a real button, deliberately styled exactly like the
          // static pill (no affordance beyond the cursor; the menu is a bit
          // of an easter egg). The epilogue's flush-left presentation is a
          // minimal readout riding the capabilities line, not a control, so
          // interactivity drops there.
          const interactive =
            onCurrentYearPillClick !== undefined && !currentYearPillFlushLeft
          const pillStyle = {
            left: lerpByDropProgress(CURRENT_YEAR_PILL_LEFT_PX, droppedLeft),
            top: lerpByDropProgress(CURRENT_YEAR_PILL_TOP_PX, droppedTop),
            // Rides the drop progress toward -100% so the pill's BOTTOM
            // ends anchored at the gap-lifted point above the line.
            transform: pillDropProgress > 0
              ? `translateY(${-100 * pillDropProgress}%)`
              : undefined,
            padding: `${CURRENT_YEAR_PILL_V_PADDING_PX}px ${CURRENT_YEAR_PILL_H_PADDING_PX}px`,
            background: currentYearPillFlushLeft
              ? 'transparent'
              : CURRENT_YEAR_PILL_BACKGROUND,
            transition:
              `background-color ${EPILOGUE_GRAPH_MOVE_DURATION_MS}ms ${EPILOGUE_MOVE_EASING}`,
          }
          const pillYear = (
            <>
              {currentYearPillMonthIndex !== undefined && (
                // Month abbreviations are all 3 letters, so the mono pill
                // keeps a constant width as the month flips.
                <span>{CURRENT_YEAR_PILL_MONTH_ABBREVIATIONS[currentYearPillMonthIndex]} </span>
              )}
              <NumberFlow
                value={currentYearPillYear}
                format={{ useGrouping: false }} transformTiming={TICKER_NUMBER_FLOW_TIMING} spinTiming={TICKER_NUMBER_FLOW_TIMING} />
            </>
          )
          // data-hud-year-pill: takeoff element for the metric-details year
          // flight (DetailsYearPill) — on open it glides onto the panel
          // header's readout ([data-metric-panel-year]), morphing from this
          // 14px tier to the panel's 17px tier mid-glide (keep
          // DetailsYearPill's GRAPH_TIER in sync with the styling here).
          return interactive ? (
          <button
            type="button"
            data-hud-year-pill
            aria-haspopup="menu"
            aria-expanded={currentYearPillMenuOpen}
            aria-label="Jump to a year"
            className="absolute font-mono font-bold cursor-pointer rounded-[6px] text-black text-[14px] z-[3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-black"
            style={pillStyle}
            onClick={(e) => {
              // The whole graph is a metric-details trigger (see
              // useMetricTrigger in HudExtrapolatedGraphBlock) — don't let
              // the pill click bubble into it.
              e.stopPropagation()
              onCurrentYearPillClick()
            }}
          >
            {pillYear}
          </button>
          ) : (
          <div
            data-hud-year-pill
            className="absolute font-mono font-bold pointer-events-none rounded-[6px] text-black text-[14px] z-[3]"
            style={pillStyle}
          >
            {pillYear}
          </div>
          )
        })()}
        {currentValueLabel && currentValueLabelPosition && (
          <div
            className="absolute text-[10px] tracking-wide whitespace-nowrap pointer-events-none"
            style={{
              left: currentValueLabelPosition.x,
              top: currentValueLabelPosition.y - 10,
              transform: 'translate(calc(-100% - 6px), -50%)',
              zIndex: 1,
              textShadow: currentValueLabelStroke,
            }}
          >
            <span>
              <NumberFlow
                value={currentValueLabel.numberValue}
                format={{
                  style: 'decimal',
                  maximumFractionDigits: currentValueLabel.maximumFractionDigits,
                }} transformTiming={TICKER_NUMBER_FLOW_TIMING} spinTiming={TICKER_NUMBER_FLOW_TIMING} />
              {currentValueLabel.suffix}
            </span>
          </div>
        )}
        {/* Annotation/hover label layer, rendered absolutely inside the graph
            container (which is position: relative) so the labels live in the
            same coordinate space as the SVG and move in true lockstep with it
            on scroll/resize — no portal, no getBoundingClientRect re-measure,
            so nothing can "swim" relative to the graph. Labels overhang the
            container (e.g. the rotated "AI TAKEOVER RISK" label to the left);
            no ancestor clips them in any current layout. */}
        <div
          className="absolute inset-0 font-mono text-[12px] pointer-events-none"
          style={{ color: textColor, zIndex: GRAPH_LABEL_LAYER_Z_INDEX }}
        >
          {labelLayerContent}
        </div>
      </div>
    </div>
  )
}
